import { LIMIT, LIMIT_MAX } from '@/constants';
import { fetcher } from '@/services/graphql/fetcher/index';
import { Sort, type AnyObj } from '@/types';
import {
  fetchCancelledOrderDocument,
  fetchConfirmReceiptOfGoodsDocument,
  fetchDeleteOrderDocument,
  fetchLogisticsDocument,
  fetchMerchantVoluntaryRefundByOrderDocument,
  fetchOrderDetailsDocument,
  fetchOrderInfoByPaymentCodeDocument,
  fetchOrderListDocument,
  fetchPendingOrdersNumberDocument,
  fetchSubscriptionCompRecordsDocument,
} from '../graphql/docs';

/**
 * 获取订单列表
 * @param skip - 数据略过个数
 */
export const fetchOrderList = async (skip: number, filterState: AnyObj, take?: number) => {
  const res = await fetcher.query(fetchOrderListDocument, {
    variables: {
      options: {
        take: take ?? LIMIT,
        skip: LIMIT * skip,
        filter: {
          state: filterState,
          isHidden: {
            eq: false,
          },
        },
        sort: {
          timeToPlaceOrder: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.orders?.items;
};

/**
 * 获取订单详情
 * @param id - 订单id
 */
export const fetchOrderDetails = async (id: string) => {
  const res = await fetcher.query(fetchOrderDetailsDocument, {
    variables: {
      id,
    },
  });
  return res.data.value?.order;
};

/**
 * 根据商户交易id获取订单信息
 * @param paymentCode - 商户交易id
 */
export const fetchOrderInfoByPaymentCode = async (paymentCode: string) => {
  const res = await fetcher.query(fetchOrderInfoByPaymentCodeDocument, {
    variables: {
      paymentCode,
    },
  });
  return res.data.value?.getOrderTypeByPaymentCode;
};

/**
 * 获取订单关联的礼品信息
 * @param orderId - 订单id
 */
export const fetchCompRecords = async (orderId: string) => {
  const res = await fetcher.query(fetchSubscriptionCompRecordsDocument, {
    variables: {
      subscriptionId: '',
      orderId,
      options: {
        take: LIMIT_MAX,
        sort: {
          createdAt: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.subscriptionCompRecords?.items;
};

/**
 * 取消订单
 * @param orderId - 订单id
 */
export const fetchCancelOrder = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelledOrderDocument, {
    orderId,
  });
  return res.data?.cancelledOrder;
};

/**
 * 删除订单
 * @param orderId - 订单id
 */
export const fetchDeleteOrder = async (orderId: string) => {
  const res = await fetcher.mutation(fetchDeleteOrderDocument, {
    orderId,
  });
  return res.data?.deleteOrder?.result;
};

/**
 * 确认收货
 * @param orderId - 订单id
 */
export const fetchConfirmReceiptOfGoods = async (orderId: string) => {
  const res = await fetcher.mutation(fetchConfirmReceiptOfGoodsDocument, {
    orderId,
  });
  return res.data?.confirmReceiptOfGoods;
};

/**
 * 查询订单栏各待办数量
 */
export const fetchPendingOrdersNumber = async () => {
  const res = await fetcher.query(fetchPendingOrdersNumberDocument, {});
  return res.data.value?.pendingOrdersNumber;
};

/**
 * 获取物流详情
 * @param orderId - 订单id
 */
export const fetchOrderLogistics = async (orderId: string) => {
  const res = await fetcher.query(fetchLogisticsDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.logistics;
};

/**
 * 获取商家主动退款信息
 * @param orderId - 订单id
 */
export const fetchMerchantVoluntaryRefundByOrder = async (orderId: string) => {
  const res = await fetcher.query(fetchMerchantVoluntaryRefundByOrderDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.merchantVoluntaryRefundByOrder;
};
